import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SettingsPage — teacher personalization preferences.
 *
 * Currently exposes the two fields the PDF cover-page dialog reads from:
 *   • default_teacher_name — pre-fills the cover-page "Teacher Name"
 *   • default_school_year  — surfaced on generated syllabi and cover pages
 */
export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacherName, setTeacherName] = useState('');
  const [schoolYear, setSchoolYear] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Hydrate from the authenticated user record — user.preferences is the
    // authoritative source, falling back to sensible defaults.
    const prefs = user?.preferences || {};
    setTeacherName(prefs.default_teacher_name || user?.name || '');
    const y = new Date().getFullYear();
    setSchoolYear(prefs.default_school_year || `${y}\u2013${y + 1} School Year`);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/preferences`, {
        default_teacher_name: teacherName,
        default_school_year: schoolYear,
      });
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-[#5A6577] hover:text-[#14213D] mb-4 -ml-2"
          data-testid="settings-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        <div className="mb-6 flex items-center gap-3">
          <User className="h-5 w-5 text-[#7A93AC]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Your workspace</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">Preferences</h1>
          </div>
        </div>

        <Card className="p-6 space-y-6">
          <div>
            <h2 className="font-serif text-lg text-[#14213D] mb-1">Cover page defaults</h2>
            <p className="text-sm text-[#5A6577] mb-4">
              These values pre-fill the PDF cover-page dialog and the syllabus generator, so downloads are one click instead of two.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="default-teacher-name" className="text-xs uppercase tracking-wider text-[#14213D]">
                  Default teacher name
                </Label>
                <Input
                  id="default-teacher-name"
                  value={teacherName}
                  onChange={(e) => setTeacherName(e.target.value)}
                  placeholder="e.g., Mr. Casteel"
                  className="mt-1"
                  data-testid="prefs-teacher-name-input"
                  maxLength={80}
                />
              </div>
              <div>
                <Label htmlFor="default-school-year" className="text-xs uppercase tracking-wider text-[#14213D]">
                  Default school year
                </Label>
                <Input
                  id="default-school-year"
                  value={schoolYear}
                  onChange={(e) => setSchoolYear(e.target.value)}
                  placeholder="e.g., 2025-2026 School Year"
                  className="mt-1"
                  data-testid="prefs-school-year-input"
                  maxLength={40}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#14213D] text-[#FDFBF7] hover:bg-[#0f172a]"
              data-testid="prefs-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving&hellip;</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Save preferences</>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
